import { LineType, registerOverlay } from 'klinecharts'
import type { OverlayFigure } from 'klinecharts'

/**
 * The Backtest chart's trade overlays. klinecharts has no marker primitive, so
 * the arrows lightweight-charts drew via `createSeriesMarkers` become overlays,
 * and the entry→exit connector is a second one.
 *
 * `btTradeMarker` anchors to the bar's low (buy) or high (sell) to reproduce the
 * Backtest tab's `belowBar`/`aboveBar` placement, and carries the count badge
 * grouped orders show. The same overlays draw the walkthrough's trades on the
 * Workbench canvas (see replayOrderLayer.ts).
 */

export const TRADE_MARKER = 'btTradeMarker'
export const TRADE_CONNECTOR = 'btTradeConnector'
export const ORDER_LINES = 'btOrderLines'

/** Buy / sell direction colours. Financial data, so green/red is the right vocabulary. */
const DIR_BUY = '#089981'
const DIR_SELL = '#f23645'
/** Stop-loss red / take-profit green. */
const LEVEL_SL = '#f23645'
const LEVEL_TP = '#089981'
/** The active-order line: a dot-dash "this order is working" stroke. */
const DOT_DASH = [2, 3, 8, 3]
/** The protective levels: a plain dash, so entry and levels never read as the same line. */
const DASH = [4, 4]
/** Non-hovered trades fade to this while another trade's set is lit. */
const DIM_ALPHA = 0.22
/**
 * Above this y the label flips BELOW its line. Drawn above near the top edge it runs under
 * the OHLC readout row and clips against the pane edge.
 */
const LABEL_FLIP_Y = 34

function withAlpha(hex: string, alpha: number): string {
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
    .toString(16)
    .padStart(2, '0')
  return `${hex}${a}`
}

export interface OrderLinesExtendData {
  dir?: 'buy' | 'sell'
  /** Fade this set because another one is hovered. */
  dimmed?: boolean
  /**
   * Whether the position actually carries these levels.
   *
   * The overlay is always built with three points so the point count never varies — a
   * missing level would otherwise shift take-profit into the stop-loss slot and have it
   * drawn in red. An absent level parks its point on the entry price and is skipped here.
   */
  hasSl?: boolean
  hasTp?: boolean
  entryLabel?: string
  slLabel?: string
  tpLabel?: string
  /** Decimals for the price-axis tag. */
  precision?: number
}

export interface TradeMarkerExtendData {
  dir?: 'up' | 'down'
  color?: string
  /** Order count for a grouped marker; empty for a single order. */
  text?: string
}

export interface TradeConnectorExtendData {
  color?: string
}

let registered = false

export function registerBacktestChartOverlays(): void {
  if (registered) return
  registered = true

  registerOverlay({
    name: TRADE_MARKER,
    totalStep: 2,
    lock: true,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates, overlay }) => {
      if (!coordinates[0]) return []
      const { x, y } = coordinates[0]
      const ext = (overlay.extendData ?? {}) as TradeMarkerExtendData
      const color = ext.color ?? '#9E9E9E'
      const up = ext.dir !== 'down'

      // Arrow glyph: a 13px head + stem, 4px clear of the bar so it reads like
      // the old above/below-bar series markers. Buy points up from below the
      // low; sell points down from above the high.
      const s = up ? 1 : -1
      const coords = [
        { x, y: y + 4 * s },
        { x: x + 6.5, y: y + 11 * s },
        { x: x + 2.5, y: y + 11 * s },
        { x: x + 2.5, y: y + 17 * s },
        { x: x - 2.5, y: y + 17 * s },
        { x: x - 2.5, y: y + 11 * s },
        { x: x - 6.5, y: y + 11 * s },
      ]

      const figures: OverlayFigure[] = [
        {
          type: 'polygon',
          attrs: { coordinates: coords },
          styles: { style: 'fill', color },
        },
      ]

      // Grouped orders get a count badge just beyond the arrow tip.
      if (ext.text) {
        figures.push({
          type: 'text',
          attrs: {
            x,
            y: y + 26 * s,
            text: ext.text,
            align: 'center',
            baseline: 'middle',
          },
          styles: {
            color: '#ffffff',
            backgroundColor: color,
            size: 10,
            paddingLeft: 4,
            paddingRight: 4,
            paddingTop: 1,
            paddingBottom: 1,
            borderRadius: 2,
          },
        })
      }

      return figures
    },
  })

  // Entry→exit connector for a closed order: a dashed segment PnL-coloured to
  // match the exit arrow, so a trade reads as one thing rather than two ticks.
  // Same visual language as the Workbench's own entry→exit connector.
  //
  // Registered rather than reusing the built-in 'segment' the Workbench uses,
  // because `ignoreEvent` is a FIGURE-level property and can only be set from
  // createPointFigures. Without it a locked segment still swallows clicks on the
  // line, so clicking a connector would do nothing instead of opening the bar's
  // order tooltip (which this chart drives from a DOM click + convertFromPixel).
  registerOverlay({
    name: TRADE_CONNECTOR,
    totalStep: 3, // two points
    lock: true,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates, overlay }) => {
      if (coordinates.length < 2) return []
      const ext = (overlay.extendData ?? {}) as TradeConnectorExtendData
      return [
        {
          type: 'line',
          attrs: { coordinates },
          styles: {
            style: LineType.Dashed,
            dashedValue: [2, 3, 8, 3],
            color: ext.color ?? '#9E9E9E',
            size: 1,
          },
          ignoreEvent: true,
        },
      ]
    },
  })

  /**
   * A LIVE order's working lines, MT5-style: while the position is open, its entry, its
   * stop-loss and its take-profit are full-width rules across the pane. The instant it
   * closes they are gone, and the entry/exit arrows plus their connector take over — which
   * is the point. A chart where a closed trade left its levels behind would show a wall of
   * historical stops that protect nothing.
   *
   * ONE overlay per position, so its three lines stay grouped and several concurrent trades
   * read as several labelled sets rather than six loose rules. Always created with three
   * points, `[entry, sl, tp]`; a missing level simply has no coordinate and draws nothing.
   *
   * Labels sit at the left edge (role + distance), and a single price tags the axis — the
   * entry. With three trades open, tagging all three levels stacks nine tags into a wall,
   * and the trades pane already holds the exact prices.
   *
   * Locked and watch-only: the replay owns the chart, so there is nothing here to drag.
   * Deliberately NOT `ignoreEvent` on the entry line, which is the hover target that lights
   * one trade's set and dims the rest.
   */
  registerOverlay({
    name: ORDER_LINES,
    totalStep: 4, // three points
    lock: true,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ overlay, coordinates, bounding }) => {
      if (!coordinates.length || !coordinates[0]) return []
      const ext = (overlay.extendData ?? {}) as OrderLinesExtendData
      const dim = (c: string) => (ext.dimmed ? withAlpha(c, DIM_ALPHA) : c)
      const dirColor = dim(ext.dir === 'sell' ? DIR_SELL : DIR_BUY)
      const sl = dim(LEVEL_SL)
      const tp = dim(LEVEL_TP)
      const w = bounding.width

      const line = (y: number, color: string, dashedValue: number[]): OverlayFigure => ({
        type: 'line',
        attrs: { coordinates: [{ x: 0, y }, { x: w, y }] },
        styles: { style: LineType.Dashed, dashedValue, color, size: 1 },
      })
      const label = (y: number, text: string, color: string): OverlayFigure => ({
        type: 'text',
        attrs:
          y < LABEL_FLIP_Y
            ? { x: 4, y: y + 3, text, align: 'left', baseline: 'top' }
            : { x: 4, y: y - 2, text, align: 'left', baseline: 'bottom' },
        styles: {
          color,
          size: 11,
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          borderSize: 0,
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
        },
      })

      const showSl = ext.hasSl !== false && !!coordinates[1]
      const showTp = ext.hasTp !== false && !!coordinates[2]

      const figures: OverlayFigure[] = [line(coordinates[0].y, dirColor, DOT_DASH)]
      if (showSl) figures.push(line(coordinates[1].y, sl, DASH))
      if (showTp) figures.push(line(coordinates[2].y, tp, DASH))
      figures.push(label(coordinates[0].y, ext.entryLabel ?? 'Entry', dirColor))
      if (showSl) figures.push(label(coordinates[1].y, ext.slLabel || 'S/L', sl))
      if (showTp) figures.push(label(coordinates[2].y, ext.tpLabel || 'T/P', tp))
      return figures
    },
    createYAxisFigures: ({ overlay, coordinates }) => {
      if (!coordinates[0]) return []
      const ext = (overlay.extendData ?? {}) as OrderLinesExtendData
      const precision = ext.precision ?? 5
      const color = ext.dimmed
        ? withAlpha(ext.dir === 'sell' ? DIR_SELL : DIR_BUY, DIM_ALPHA)
        : ext.dir === 'sell'
          ? DIR_SELL
          : DIR_BUY
      const value = overlay.points[0]?.value
      if (value == null) return []
      return [
        {
          type: 'text',
          attrs: {
            x: 0,
            y: coordinates[0].y,
            text: Number(value).toFixed(precision),
            align: 'left',
            baseline: 'middle',
          },
          styles: {
            color: ext.dimmed ? withAlpha('#ffffff', 0.5) : '#ffffff',
            backgroundColor: color,
            size: 11,
            paddingLeft: 4,
            paddingRight: 4,
            paddingTop: 2,
            paddingBottom: 2,
            borderRadius: 2,
          },
        },
      ]
    },
  })
}
